/**
 * ENT-5889 (HF-05): one guard for every sink that renders or follows a stored `action_url`.
 * `notification_deliveries.action_url` is data, not trusted markup: rendering it straight into
 * `<Link to>` / `href` lets a `javascript:` / `data:` value (or an external host) reach the browser.
 * Parse it with the URL parser (never a regex) against the current origin.
 */

function isTrustedAppHost(hostname: string): boolean {
  return hostname === 'app.supply-guard.io'
    || hostname === 'staging.supply-guard.io'
    || hostname === 'enterprise-staging.vercel.app'
    || hostname.endsWith('.vercel.app');
}

export type ActionUrlTarget =
  | { kind: 'internal'; to: string }
  | { kind: 'external'; to: string }
  | { kind: 'blocked' };

/**
 * Classifies a stored action URL. Only http(s) is ever followed; same-origin and trusted app
 * hosts become an in-app path, any other http(s) host is `external`, everything else (javascript:,
 * data:, vbscript:, unparseable) is `blocked`. An empty value falls back to `fallback`.
 */
export function resolveActionUrl(actionUrl: string | null | undefined, fallback = '/'): ActionUrlTarget {
  const raw = (actionUrl ?? '').trim();
  if (!raw) return { kind: 'internal', to: fallback };
  let resolved: URL;
  try {
    resolved = new URL(raw, window.location.origin);
  } catch {
    return { kind: 'blocked' };
  }
  if (!['http:', 'https:'].includes(resolved.protocol)) return { kind: 'blocked' };
  if (resolved.origin === window.location.origin || isTrustedAppHost(resolved.hostname)) {
    return { kind: 'internal', to: `${resolved.pathname}${resolved.search}${resolved.hash}` };
  }
  return { kind: 'external', to: resolved.toString() };
}

/**
 * An in-app path for a stored action URL, or null. For sinks that must never leave the app
 * (a rendered `<Link>`): external hosts and every non-http(s) scheme yield null.
 */
export function internalActionPath(actionUrl: string | null | undefined): string | null {
  if (!actionUrl || !actionUrl.trim()) return null;
  const target = resolveActionUrl(actionUrl);
  return target.kind === 'internal' ? target.to : null;
}
